import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

const TAB_EARNINGS = 'earnings';
const TAB_SALES = 'sales';

function parsePickupToken(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  // If it looks like a URL with ?t= or &t=, extract token
  try {
    const url = trimmed.startsWith('http') ? new URL(trimmed) : new URL(trimmed, 'http://dummy');
    const t = url.searchParams.get('t');
    if (t) return t;
  } catch (_) {}
  // Otherwise treat as raw token (GUID)
  return trimmed.length > 10 ? trimmed : null;
}

export default function VendorReports() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TAB_EARNINGS);
  const [earnings, setEarnings] = useState(null);
  const [sales, setSales] = useState([]);
  const [earningsLoading, setEarningsLoading] = useState(true);
  const [salesLoading, setSalesLoading] = useState(true);
  const [markingPickedUpId, setMarkingPickedUpId] = useState(null);
  const [showScanMock, setShowScanMock] = useState(false);
  const [scanMockInput, setScanMockInput] = useState('');
  const [scanMockError, setScanMockError] = useState('');

  const fetchEarnings = useCallback(async () => {
    setEarningsLoading(true);
    try {
      const res = await api('/api/sales/vendor/earnings', { getToken });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      const data = await res.json();
      setEarnings(data);
    } catch (err) {
      console.error('Failed to fetch earnings:', err);
    } finally {
      setEarningsLoading(false);
    }
  }, [getToken]);

  const fetchSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const res = await api('/api/sales/vendor', { getToken });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      const data = await res.json();
      setSales(data);
    } catch (err) {
      console.error('Failed to fetch sales:', err);
    } finally {
      setSalesLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchEarnings();
    fetchSales();
  }, [fetchEarnings, fetchSales]);

  const handleOpenScanMock = () => {
    setShowScanMock(true);
    setScanMockInput('');
    setScanMockError('');
  };

  const handleGoToPickupFromMock = () => {
    const token = parsePickupToken(scanMockInput);
    setScanMockError('');
    if (!token) {
      setScanMockError('Paste the full pickup link (e.g. https://.../pickup?t=...) or the token from the customer\'s QR.');
      return;
    }
    setShowScanMock(false);
    navigate(`/pickup?t=${encodeURIComponent(token)}`);
  };

  const handleMarkPickedUp = async (saleId) => {
    setMarkingPickedUpId(saleId);
    try {
      const res = await api(`/api/sales/${saleId}/mark-picked-up`, {
        getToken,
        method: 'PATCH',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }

      fetchSales();
      fetchEarnings();
    } catch (err) {
      alert(`Failed to mark as picked up: ${err.message}`);
    } finally {
      setMarkingPickedUpId(null);
    }
  };

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold">Reports</h1>
          <button
            type="button"
            onClick={handleOpenScanMock}
            className="h-12 px-5 rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors shadow-[0_4px_0_0_#3730a3] active:shadow-none active:translate-y-1"
          >
            Scan customer QR
          </button>
        </div>

        {/* Demo scan: paste link (phone camera / QR scan opens the same /pickup?t= URL) */}
        {showScanMock && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowScanMock(false)}>
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-2">Open customer pickup</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
                On phone: scan the buyer&apos;s QR (camera opens the pickup page). On PC for demo: paste the link from My Purchases → Copy pickup link.
              </p>
              <label className="block text-sm font-medium mb-1">Pickup link or token</label>
              <input
                type="text"
                value={scanMockInput}
                onChange={(e) => { setScanMockInput(e.target.value); setScanMockError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleGoToPickupFromMock(); }}
                autoFocus
                placeholder="https://.../pickup?t=... or paste token"
                className="w-full h-12 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500"
              />
              {scanMockError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{scanMockError}</p>}
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowScanMock(false)} className="flex-1 h-12 rounded-lg border border-slate-300 dark:border-slate-600 font-medium">
                  Cancel
                </button>
                <button type="button" onClick={handleGoToPickupFromMock} className="flex-1 h-12 rounded-lg font-semibold bg-indigo-600 text-white hover:bg-indigo-700">
                  Open pickup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 p-1">
          <button
            type="button"
            onClick={() => setActiveTab(TAB_EARNINGS)}
            className={`flex-1 py-3 px-4 rounded-lg text-lg font-semibold transition-colors ${
              activeTab === TAB_EARNINGS
                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            }`}
          >
            Earning Summary
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(TAB_SALES)}
            className={`flex-1 py-3 px-4 rounded-lg text-lg font-semibold transition-colors ${
              activeTab === TAB_SALES
                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            }`}
          >
            Sales
          </button>
        </div>

        {/* Earning Summary tab */}
        {activeTab === TAB_EARNINGS && (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-6">
            {earningsLoading ? (
              <p className="text-lg text-slate-500">Loading earnings…</p>
            ) : earnings ? (
              <div className="rounded-xl border-2 border-indigo-300 dark:border-indigo-600 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-bold mb-6 text-indigo-900 dark:text-indigo-100">Earnings Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-4 md:p-6 shadow-md">
                    <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 mb-2">Total Sales</p>
                    <p className="text-3xl md:text-4xl font-bold text-indigo-600 dark:text-indigo-400">{earnings.totalSalesCount}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-4 md:p-6 shadow-md">
                    <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 mb-2">Sales Amount</p>
                    <p className="text-3xl md:text-4xl font-bold text-green-600 dark:text-green-400">${parseFloat(earnings.totalGross).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-lg text-slate-500">No earnings data available.</p>
            )}
          </div>
        )}

        {/* Sales tab */}
        {activeTab === TAB_SALES && (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold">Sales</h2>
              {sales.filter(s => s.status !== 'PickedUp').length > 0 && (
                <span className="px-3 py-1 rounded-full bg-red-500 text-white text-sm font-semibold">
                  {sales.filter(s => s.status !== 'PickedUp').length} Pending
                </span>
              )}
            </div>
            {salesLoading ? (
              <p className="text-lg text-slate-500">Loading sales…</p>
            ) : sales.length === 0 ? (
              <p className="text-lg text-slate-500">No sales yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-slate-300 dark:border-slate-600">
                      <th className="text-left p-3 text-sm md:text-base font-semibold">Item</th>
                      <th className="text-left p-3 text-sm md:text-base font-semibold">Amount</th>
                      <th className="text-left p-3 text-sm md:text-base font-semibold">Status</th>
                      <th className="text-left p-3 text-sm md:text-base font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) => (
                      <tr key={sale.id} className="border-b border-slate-200 dark:border-slate-700">
                        <td className="p-3 text-sm md:text-base">{sale.itemTitle}</td>
                        <td className="p-3 text-sm md:text-base font-semibold">${parseFloat(sale.amount).toFixed(2)}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs md:text-sm font-medium ${
                            sale.status === 'PickedUp'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                              : sale.status === 'PendingPickup'
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                          }`}>
                            {sale.status}
                          </span>
                        </td>
                        <td className="p-3">
                          {sale.status !== 'PickedUp' && (
                            <button
                              type="button"
                              onClick={() => handleMarkPickedUp(sale.id)}
                              disabled={markingPickedUpId === sale.id}
                              className="px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {markingPickedUpId === sale.id ? 'Marking…' : 'Mark picked up'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </SignedIn>
  );
}
